import { postCalendarService } from '../core/services'
import * as moment from 'moment'
import * as React from 'react'

export const CALENDAR_EVENTS_DEFAULT_MONTHS = 2
export const CALENDAR_EVENTS_INITIAL_MONTHS = 6

export interface ICalendarItem {
  date: moment.Moment
  start_times: any[]
}

interface ICalendarEventsParams {
  start_at: string
  end_at: string
}

interface ICalendarEventsOptions {
  includePast?: boolean
  onFetchComplete?: (calendarEvents: ICalendarItem[]) => void
}

interface IFetchedRange {
  start: moment.Moment
  end: moment.Moment
}

export interface IDate {
  date: moment.Moment
  startTimes: any[]
}

interface IUseCalendarEventsReturn {
  calendarEvents: ICalendarItem[]
  fetching: boolean
  availableDates: IDate[]
  getCalendarEvents: (
    postId: number | string,
    startAt: moment.Moment,
    months: number,
    options?: ICalendarEventsOptions
  ) => Promise<ICalendarItem[]>
}

/**
 * start_timesの一意性を判定するためのキーを生成する
 * @param startTime start_timesのオブジェクト
 * @returns 一意性を判定するためのキー
 */
const startTimeKey = (startTime: any): string => {
  return [
    startTime.post_id,
    startTime.start_time,
    startTime.post_calendar_id == null
      ? 'post_calendar:null'
      : `post_calendar:${startTime.post_calendar_id}`,
    startTime.post_start_time_id == null
      ? 'post_start_time:null'
      : `post_start_time:${startTime.post_start_time_id}`,
  ].join('|')
}

/**
 * 2つのカレンダーアイテムの配列をマージし、同じ日付のアイテムはstart_timesを結合する
 * 日付キー・start_timesキー
 * @param baseCalendarItems 基本となるカレンダーアイテムの配列
 * @param nextCalendarItems 追加するカレンダーアイテムの配列
 * @returns マージ後のカレンダーアイテムの配列
 */
const mergeCalendarItems = (
  baseCalendarItems: ICalendarItem[],
  nextCalendarItems: ICalendarItem[]
): ICalendarItem[] => {
  const calendarItemsByDay: Record<string, ICalendarItem> = {}
  const mergedCalenderItems = [...baseCalendarItems, ...nextCalendarItems]
  mergedCalenderItems.forEach(calendarItem => {
    const dayKey = calendarItem.date.format('YYYY-MM-DD')
    const current = calendarItemsByDay[dayKey] || {
      date: calendarItem.date.clone(),
      start_times: [],
    }
    const startTimeKeys = current.start_times.reduce((keys, startTime) => {
      keys[startTimeKey(startTime)] = true
      return keys
    }, {})

    calendarItem.start_times.forEach(startTime => {
      const key = startTimeKey(startTime)
      if (!startTimeKeys[key]) {
        current.start_times.push(startTime)
        startTimeKeys[key] = true
      }
    })

    calendarItemsByDay[dayKey] = current
  })

  const sortedCalendarItems = Object.keys(calendarItemsByDay)
    .sort()
    .map(dayKey => calendarItemsByDay[dayKey])

  sortedCalendarItems.forEach(calendarItem => {
    calendarItem.start_times.sort((a, b) => a.start_time.localeCompare(b.start_time))
  })

  return sortedCalendarItems
}

const calculateAvailableDates = (calendarData: ICalendarItem[]): IDate[] => {
  return calendarData
    .filter(data =>
      data.start_times.find(startTime => startTime.available_count !== 0 && !startTime.reserved)
    )
    .map(data => ({
      date: data.date,
      startTimes: data.start_times,
    }))
}

const postIdKey = (postId: number | string): string => String(postId)

/**
 * 指定された月の範囲が、すでに取得済みの範囲に含まれているかどうかを判定する
 * @param ranges 取得済みの範囲の配列
 * @param monthStart 判定する月の開始日
 * @param monthEnd 判定する月の終了日
 * @returns 指定された月の範囲が取得済みの範囲に含まれている場合はtrue、それ以外はfalse
 */
const isMonthCovered = (
  ranges: IFetchedRange[],
  monthStart: moment.Moment,
  monthEnd: moment.Moment
): boolean => {
  return ranges.some(
    range =>
      range.start.isSameOrBefore(monthStart, 'day') && range.end.isSameOrAfter(monthEnd, 'day')
  )
}

/**
 * 指定された開始日から指定された月数の範囲を返す
 * @param startAt 開始日
 * @param months 取得する月数
 * @returns 取得する範囲の開始日と終了日
 */
const getCalendarEventsRange = (startAt: moment.Moment, months: number): IFetchedRange => {
  const normalizedMonths = Math.max(1, months)
  const start = startAt.clone().startOf('month')
  const end = start
    .clone()
    .add(normalizedMonths - 1, 'month')
    .endOf('month')

  return { start, end }
}

/**
 * 指定された期間の中で、すでに取得済みの範囲を除外した未取得の範囲を返す
 * @param startAt 開始日
 * @param endAt 終了日
 * @param excludedRanges 除外する取得済みの範囲
 * @returns 未取得の範囲の配列
 */
const getMissingRanges = (
  startAt: moment.Moment,
  endAt: moment.Moment,
  excludedRanges: IFetchedRange[]
): IFetchedRange[] => {
  const missingRanges: IFetchedRange[] = []
  const cursor = startAt.clone().startOf('month')
  const lastMonth = endAt.clone().startOf('month')
  let currentRange: IFetchedRange | null = null

  while (cursor.isSameOrBefore(lastMonth, 'month')) {
    const monthStart = cursor.clone().startOf('month')
    const monthEnd = cursor.clone().endOf('month')
    const covered = isMonthCovered(excludedRanges, monthStart, monthEnd)

    if (covered) {
      if (currentRange) {
        missingRanges.push(currentRange)
        currentRange = null
      }
    } else if (currentRange) {
      currentRange.end = monthEnd
    } else {
      currentRange = {
        start: monthStart,
        end: monthEnd,
      }
    }

    cursor.add(1, 'month')
  }

  if (currentRange) {
    missingRanges.push(currentRange)
  }

  return missingRanges
}

/**
 * カレンダーイベント管理のためのカスタムフック
 */
export const useCalendarEvents = (): IUseCalendarEventsReturn => {
  const [calendarEvents, setCalendarEvents] = React.useState<ICalendarItem[]>([])
  const [fetching, setFetching] = React.useState(false)
  const calendarEventsRef = React.useRef<ICalendarItem[]>([])
  const fetchedRangesRef = React.useRef<Record<string, IFetchedRange[]>>({})
  const fetchingRangesRef = React.useRef<Record<string, IFetchedRange[]>>({})
  const pendingRequestCountRef = React.useRef(0)
  const mountedRef = React.useRef(true)

  React.useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const convertToCalendarItems = React.useCallback(
    (
      calendars: Array<{
        day: string
        start_times: any[]
      }>
    ): ICalendarItem[] => {
      return calendars.map(({ day, start_times }) => ({
        date: moment(day),
        start_times,
      }))
    },
    []
  )

  const updateCalendarEvents = React.useCallback((nextCalendarEvents: ICalendarItem[]) => {
    calendarEventsRef.current = nextCalendarEvents

    if (!mountedRef.current) {
      return nextCalendarEvents
    }

    setCalendarEvents(nextCalendarEvents)

    return nextCalendarEvents
  }, [])

  const fetchCalendarItems = React.useCallback(
    async (
      postId: number | string,
      startAt: moment.Moment,
      endAt: moment.Moment,
      includePast: boolean
    ): Promise<ICalendarItem[]> => {
      const params: ICalendarEventsParams = {
        start_at: startAt.format('YYYY-MM-DD'),
        end_at: endAt.format('YYYY-MM-DD'),
      }

      const { calendars } = includePast
        ? await postCalendarService.getCalendarsWithPast(postId, params)
        : await postCalendarService.getCalendars(postId, params)

      return convertToCalendarItems(calendars || [])
    },
    [convertToCalendarItems]
  )

  const mergeCalendarEvents = React.useCallback(
    (nextCalendarEvents: ICalendarItem[]) => {
      const mergedCalendarEvents = mergeCalendarItems(calendarEventsRef.current, nextCalendarEvents)
      return updateCalendarEvents(mergedCalendarEvents)
    },
    [updateCalendarEvents]
  )

  const addFetchingRanges = React.useCallback((key: string, ranges: IFetchedRange[]) => {
    fetchingRangesRef.current = {
      ...fetchingRangesRef.current,
      [key]: [...(fetchingRangesRef.current[key] || []), ...ranges],
    }
  }, [])

  const removeFetchingRange = React.useCallback((key: string, targetRange: IFetchedRange) => {
    fetchingRangesRef.current = {
      ...fetchingRangesRef.current,
      [key]: (fetchingRangesRef.current[key] || []).filter(
        range =>
          !range.start.isSame(targetRange.start, 'day') || !range.end.isSame(targetRange.end, 'day')
      ),
    }
  }, [])

  const markRangeFetched = React.useCallback((key: string, range: IFetchedRange) => {
    fetchedRangesRef.current = {
      ...fetchedRangesRef.current,
      [key]: [
        ...(fetchedRangesRef.current[key] || []),
        {
          start: range.start.clone().startOf('day'),
          end: range.end.clone().endOf('day'),
        },
      ],
    }
  }, [])

  /**
   * カレンダーイベントを取得する関数
   * すでに取得済みの範囲は再取得せず、必要な範囲のみを取得する
   * 取得後はカレンダーイベントをマージして更新する
   * @param postId 投稿ID
   * @param startAt 開始日
   * @param months 取得する月数
   * @param options オプション
   * @returns 取得したカレンダーイベントの配列
   */
  const getCalendarEvents = React.useCallback(
    async (
      postId: number | string,
      startAt: moment.Moment,
      months: number,
      options: ICalendarEventsOptions = {}
    ): Promise<ICalendarItem[]> => {
      const { includePast = false, onFetchComplete } = options
      const key = postIdKey(postId)
      const requestedRange = getCalendarEventsRange(startAt, months)

      const excludedRanges = [
        ...(fetchedRangesRef.current[key] || []),
        ...(fetchingRangesRef.current[key] || []),
      ]

      const missingRanges = getMissingRanges(
        requestedRange.start,
        requestedRange.end,
        excludedRanges
      )

      if (missingRanges.length === 0) {
        return calendarEventsRef.current
      }

      addFetchingRanges(key, missingRanges)
      pendingRequestCountRef.current += missingRanges.length
      if (mountedRef.current) {
        setFetching(true)
      }

      const fetchedCalendarEvents: ICalendarItem[] = []

      await Promise.all(
        missingRanges.map(async range => {
          try {
            const calendarItems = await fetchCalendarItems(
              postId,
              range.start,
              range.end,
              includePast
            )
            fetchedCalendarEvents.push(...calendarItems)
            markRangeFetched(key, range)
          } catch (error) {
            console.error('Calendar events fetch error:', error)
          } finally {
            removeFetchingRange(key, range)
            pendingRequestCountRef.current -= 1
            if (mountedRef.current && pendingRequestCountRef.current === 0) {
              setFetching(false)
            }
          }
        })
      )

      const mergedCalendarEvents =
        fetchedCalendarEvents.length > 0
          ? mergeCalendarEvents(fetchedCalendarEvents)
          : calendarEventsRef.current

      if (onFetchComplete) {
        onFetchComplete(mergedCalendarEvents)
      }

      return mergedCalendarEvents
    },
    [
      addFetchingRanges,
      fetchCalendarItems,
      markRangeFetched,
      mergeCalendarEvents,
      removeFetchingRange,
    ]
  )

  const availableDates = React.useMemo(() => calculateAvailableDates(calendarEvents), [
    calendarEvents,
  ])

  return {
    calendarEvents,
    fetching,
    availableDates,
    getCalendarEvents,
  }
}

/**
 * カレンダーの外部範囲判定ヘルパー関数
 * 複数コンポーネントで使用されている handleCalendarOutSideRange ロジックを統一
 */
export const isCalendarOutsideRangeForCalendarEvents = (
  calendarEvents: ICalendarItem[],
  calendarMoment: moment.Moment
): boolean => {
  const calendarEvent = calendarEvents.find(({ date }) => calendarMoment.isSame(date, 'day'))

  return !calendarEvent || !calendarEvent.start_times.length
}
